import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboard, getWorkOrders, reorderWorkOrders, getCapexSummary } from '../services/maintenanceService';
import ScrollHint from '../components/ScrollHint';
import { useLocationContext } from '../contexts/LocationContext';

const MaintenanceDashboard = () => {
  const { currentLocation } = useLocationContext();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [completedOrders, setCompletedOrders] = useState([]);
  const [completedLoading, setCompletedLoading] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
  const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0];
  const [completedStart, setCompletedStart] = useState(thirtyDaysAgo);
  const [completedEnd, setCompletedEnd] = useState(today);
  const [completedOpen, setCompletedOpen] = useState(false);

  // Capex section state
  const [capexData, setCapexData] = useState(null);
  const [capexLoading, setCapexLoading] = useState(false);
  const [capexStart, setCapexStart] = useState(yearStart);
  const [capexEnd, setCapexEnd] = useState(today);

  // Drag-and-drop state for open work orders
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [openOrders, setOpenOrders] = useState([]);

  // Sorting & filtering for completed table
  const [sortCol, setSortCol] = useState('completed_date');
  const [sortDir, setSortDir] = useState('desc');
  const [filterTitle, setFilterTitle] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterEquipment, setFilterEquipment] = useState('');

  useEffect(() => {
    fetchDashboard();
  }, [currentLocation?.id]);

  useEffect(() => {
    fetchCompleted();
  }, [completedStart, completedEnd, currentLocation?.id]);

  useEffect(() => {
    fetchCapex();
  }, [capexStart, capexEnd, currentLocation?.id]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const data = await getDashboard();
      setDashboard(data);
      setOpenOrders(data.open_work_orders || []);
    } catch (err) {
      setError('Failed to load maintenance dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, idx) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = async (e) => {
    e.currentTarget.style.opacity = '1';
    if (dragIndex !== null && overIndex !== null && dragIndex !== overIndex) {
      const newList = [...openOrders];
      const [dragged] = newList.splice(dragIndex, 1);
      newList.splice(overIndex, 0, dragged);
      setOpenOrders(newList);
      try {
        await reorderWorkOrders(newList.map(wo => wo.id));
      } catch (err) {
        console.error('Failed to save order', err);
      }
    }
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIndex(idx);
  };

  // Mobile: move item up/down by index
  const handleMove = async (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= openOrders.length) return;
    const newList = [...openOrders];
    const [moved] = newList.splice(fromIdx, 1);
    newList.splice(toIdx, 0, moved);
    setOpenOrders(newList);
    try {
      await reorderWorkOrders(newList.map(wo => wo.id));
    } catch (err) {
      console.error('Failed to save order', err);
    }
  };

  const fetchCompleted = async () => {
    try {
      setCompletedLoading(true);
      const data = await getWorkOrders({
        status: 'completed',
        completed_start: completedStart,
        completed_end: completedEnd,
      });
      setCompletedOrders(data.work_orders || []);
    } catch (err) {
      console.error('Failed to load completed work orders', err);
    } finally {
      setCompletedLoading(false);
    }
  };

  const fetchCapex = async () => {
    try {
      setCapexLoading(true);
      const data = await getCapexSummary({
        start_date: capexStart,
        end_date: capexEnd,
      });
      setCapexData(data);
    } catch (err) {
      console.error('Failed to load capex summary', err);
    } finally {
      setCapexLoading(false);
    }
  };

  const getEquipmentLabel = (wo) =>
    wo.equipment_area?.name || (wo.equipment_category?.length > 0 ? wo.equipment_category.join(', ') : 'General');

  const filteredCompleted = completedOrders.filter(wo => {
    if (filterTitle && !wo.title.toLowerCase().includes(filterTitle.toLowerCase())) return false;
    if (filterType && wo.type !== filterType) return false;
    if (filterEquipment && !getEquipmentLabel(wo).toLowerCase().includes(filterEquipment.toLowerCase())) return false;
    return true;
  });

  const sortedCompleted = [...filteredCompleted].sort((a, b) => {
    let av, bv;
    switch (sortCol) {
      case 'title': av = a.title.toLowerCase(); bv = b.title.toLowerCase(); break;
      case 'type': av = a.type; bv = b.type; break;
      case 'equipment': av = getEquipmentLabel(a).toLowerCase(); bv = getEquipmentLabel(b).toLowerCase(); break;
      case 'completed_date': av = a.completed_date || ''; bv = b.completed_date || ''; break;
      case 'cost': av = a.total_cost || 0; bv = b.total_cost || 0; break;
      default: av = ''; bv = '';
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const completedTotal = sortedCompleted.reduce((sum, wo) => sum + (wo.total_cost || 0), 0);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir(col === 'cost' ? 'desc' : 'asc');
    }
  };

  const SortHeader = ({ col, children }) => (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
      onClick={() => handleSort(col)}
    >
      {children} {sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : ''}
    </th>
  );

  const priorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'normal': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const typeLabel = (type) => {
    switch (type) {
      case 'repair': return 'Repair';
      case 'scheduled': return 'Scheduled';
      case 'task': return 'Task';
      case 'capex': return 'Capex';
      default: return type;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  const hasOverdue = (dashboard?.overdue_schedules?.length > 0) || (dashboard?.overdue_work_orders?.length > 0);

  return (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between items-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Maintenance</h1>
          <div className="hidden sm:flex space-x-3">
            <Link
              to="/maintenance/schedules"
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Schedules
            </Link>
            <Link
              to="/maintenance/costs"
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Cost Report
            </Link>
            <Link
              to="/maintenance/issues"
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Customer Issues
            </Link>
            <Link
              to="/maintenance/work-order/new?type=repair"
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              + New Repair
            </Link>
            <Link
              to="/maintenance/work-order/new"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              + New Task
            </Link>
            <Link
              to="/maintenance/work-order/new?type=capex"
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              + New Capex
            </Link>
            <Link
              to="/maintenance/sops"
              className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              SOPs
            </Link>
          </div>
        </div>
        <div className="sm:hidden mt-3 space-y-1.5">
          <div className="grid grid-cols-3 gap-1.5">
            <Link to="/maintenance/schedules" className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-1 py-2 rounded-lg text-xs font-medium transition-colors text-center flex items-center justify-center">
              Schedules
            </Link>
            <Link to="/maintenance/costs" className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-1 py-2 rounded-lg text-xs font-medium transition-colors text-center flex items-center justify-center">
              Cost Report
            </Link>
            <Link to="/maintenance/issues" className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-1 py-2 rounded-lg text-xs font-medium transition-colors text-center flex items-center justify-center">
              Issues
            </Link>
            <Link to="/maintenance/work-order/new?type=repair" className="bg-red-600 hover:bg-red-700 text-white px-1 py-2 rounded-lg text-xs font-medium transition-colors text-center flex items-center justify-center">
              + Repair
            </Link>
            <Link to="/maintenance/work-order/new" className="bg-blue-600 hover:bg-blue-700 text-white px-1 py-2 rounded-lg text-xs font-medium transition-colors text-center flex items-center justify-center">
              + Task
            </Link>
            <Link to="/maintenance/work-order/new?type=capex" className="bg-green-600 hover:bg-green-700 text-white px-1 py-2 rounded-lg text-xs font-medium transition-colors text-center flex items-center justify-center">
              + Capex
            </Link>
          </div>
          <Link
            to="/maintenance/sops"
            className="block bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-1 py-2 rounded-lg text-xs font-medium transition-colors text-center"
          >
            SOPs
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Overdue Alerts */}
      {hasOverdue && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Overdue Items</h2>
          <div className="space-y-1">
            {dashboard.overdue_schedules.map(s => (
              <div key={`s-${s.id}`} className="text-sm text-red-700 flex justify-between items-center">
                <span>
                  <span className="font-medium">{s.title}</span>
                  {(s.equipment_category?.length > 0 || s.equipment_area) && <span className="text-red-500 ml-1">({s.equipment_category?.length > 0 ? s.equipment_category.join(', ') : s.equipment_area?.name})</span>}
                  {' '}&mdash; scheduled maintenance overdue by {Math.abs(s.days_until_due)} days
                </span>
                <Link to="/maintenance/schedules" className="text-red-800 hover:text-red-900 underline text-xs">
                  View
                </Link>
              </div>
            ))}
            {dashboard.overdue_work_orders.map(wo => (
              <div key={`wo-${wo.id}`} className="text-sm text-red-700 flex justify-between items-center">
                <span>
                  <span className="font-medium">{wo.title}</span>
                  {wo.equipment_area && <span className="text-red-500 ml-1">({wo.equipment_area.name})</span>}
                  {' '}&mdash; {wo.type} due {formatDate(wo.due_date)}
                </span>
                <Link to={`/maintenance/work-order/${wo.id}`} className="text-red-800 hover:text-red-900 underline text-xs">
                  View
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Scheduled Maintenance */}
      {dashboard?.upcoming_schedules?.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-yellow-800 mb-2"><span className="hidden sm:inline">Upcoming </span>Scheduled Maintenance (7 days)</h2>
          <div className="space-y-1">
            {dashboard.upcoming_schedules.map(s => (
              <div key={s.id} className="text-sm text-yellow-700 flex justify-between items-center">
                <span>
                  <span className="font-medium">{s.title}</span>
                  {(s.equipment_category?.length > 0 || s.equipment_area) && <span className="text-yellow-500 ml-1">({s.equipment_category?.length > 0 ? s.equipment_category.join(', ') : s.equipment_area?.name})</span>}
                  {' '}&mdash; due in {s.days_until_due} day{s.days_until_due !== 1 ? 's' : ''} ({formatDate(s.next_due_date)})
                </span>
                <Link to="/maintenance/schedules" className="text-yellow-800 hover:text-yellow-900 underline text-xs">
                  View
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Open Work Orders */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            Open Tasks & Repairs ({openOrders.length})
          </h2>
        </div>
        {openOrders.length > 0 ? (
          <ScrollHint>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-10 px-2 py-3"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Equipment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {openOrders.map((wo, idx) => (
                  <tr
                    key={wo.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    className={`
                      hover:bg-gray-50 sm:cursor-grab sm:active:cursor-grabbing
                      ${overIndex === idx && dragIndex !== null && dragIndex !== idx ? 'border-t-2 border-blue-400' : ''}
                    `}
                  >
                    <td className="w-10 px-2 py-4 text-center select-none">
                      {/* Desktop: drag handle */}
                      <span className="hidden sm:inline text-gray-300" title="Drag to reorder">&#9776;</span>
                      {/* Mobile: up/down buttons */}
                      <div className="sm:hidden flex flex-col items-center gap-0.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleMove(idx, idx - 1); }}
                          disabled={idx === 0}
                          className={`p-0.5 ${idx === 0 ? 'text-gray-200' : 'text-gray-400 active:text-blue-600'}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleMove(idx, idx + 1); }}
                          disabled={idx === openOrders.length - 1}
                          className={`p-0.5 ${idx === openOrders.length - 1 ? 'text-gray-200' : 'text-gray-400 active:text-blue-600'}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      <Link to={`/maintenance/work-order/${wo.id}`} className="text-blue-600 hover:text-blue-800">
                        {wo.title}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {typeLabel(wo.type)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {wo.equipment_area?.name || (wo.equipment_category?.length > 0 ? wo.equipment_category.join(', ') : 'General')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${priorityColor(wo.priority)}`}>
                        {wo.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {wo.status === 'in_progress' ? 'In Progress' : 'Open'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(wo.due_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Link
                        to={`/maintenance/work-order/edit/${wo.id}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollHint>
        ) : (
          <div className="p-6 text-center text-gray-500">
            No open tasks or repairs. Everything is up to date!
          </div>
        )}
      </div>

      {/* Completed Work */}
      <div className="bg-white rounded-lg shadow">
        <button
          onClick={() => setCompletedOpen(!completedOpen)}
          className="w-full px-6 py-4 flex items-center justify-between text-left"
        >
          <h2 className="text-xl font-semibold text-gray-900">
            Completed Work
            <span className="text-sm font-normal text-gray-500 ml-2">
              ({completedOrders.length})
            </span>
          </h2>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${completedOpen ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {completedOpen && (
          <>
            <div className="px-6 py-3 border-t border-b border-gray-200">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="text-sm text-gray-500">
                  {filteredCompleted.length !== completedOrders.length && (
                    <span>Showing {filteredCompleted.length} of {completedOrders.length}</span>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="date"
                    value={completedStart}
                    onChange={(e) => setCompletedStart(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                  />
                  <span className="text-gray-500 text-sm">to</span>
                  <input
                    type="date"
                    value={completedEnd}
                    onChange={(e) => setCompletedEnd(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                  />
                </div>
              </div>
            </div>
            {completedLoading ? (
              <div className="p-6 text-center text-gray-500">Loading...</div>
            ) : completedOrders.length > 0 ? (
              <ScrollHint>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <SortHeader col="title">Title</SortHeader>
                      <SortHeader col="type">Type</SortHeader>
                      <SortHeader col="equipment">Equipment</SortHeader>
                      <SortHeader col="completed_date">Completed</SortHeader>
                      <SortHeader col="cost">Cost</SortHeader>
                    </tr>
                    <tr className="bg-gray-50">
                      <th className="px-6 py-2">
                        <input
                          type="text"
                          value={filterTitle}
                          onChange={(e) => setFilterTitle(e.target.value)}
                          placeholder="Filter..."
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-normal"
                        />
                      </th>
                      <th className="px-6 py-2">
                        <select
                          value={filterType}
                          onChange={(e) => setFilterType(e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-normal"
                        >
                          <option value="">All</option>
                          <option value="repair">Repair</option>
                          <option value="scheduled">Scheduled</option>
                          <option value="task">Task</option>
                          <option value="capex">Capex</option>
                        </select>
                      </th>
                      <th className="px-6 py-2">
                        <input
                          type="text"
                          value={filterEquipment}
                          onChange={(e) => setFilterEquipment(e.target.value)}
                          placeholder="Filter..."
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-normal"
                        />
                      </th>
                      <th className="px-6 py-2"></th>
                      <th className="px-6 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedCompleted.map(wo => (
                      <tr key={wo.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          <Link to={`/maintenance/work-order/${wo.id}`} className="text-blue-600 hover:text-blue-800">
                            {wo.title}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {typeLabel(wo.type)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {getEquipmentLabel(wo)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(wo.completed_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {wo.total_cost > 0 ? `$${wo.total_cost.toFixed(2)}` : '-'}
                        </td>
                      </tr>
                    ))}
                    {completedTotal > 0 && (
                      <tr className="bg-gray-50 font-bold">
                        <td colSpan="4" className="px-6 py-3 text-sm text-gray-900 text-right">Total Cost:</td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">${completedTotal.toFixed(2)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </ScrollHint>
            ) : (
              <div className="p-6 text-center text-gray-500">
                No completed work orders in this date range.
              </div>
            )}
          </>
        )}
      </div>
      {/* Capital Expenditures */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-xl font-semibold text-gray-900">Capital Expenditures</h2>
            <div className="flex items-center space-x-3">
              <input
                type="date"
                value={capexStart}
                onChange={(e) => setCapexStart(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
              />
              <span className="text-gray-500 text-sm">to</span>
              <input
                type="date"
                value={capexEnd}
                onChange={(e) => setCapexEnd(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
              />
            </div>
          </div>
        </div>
        {capexLoading ? (
          <div className="p-6 text-center text-gray-500">Loading...</div>
        ) : capexData && capexData.total_count > 0 ? (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-6 text-sm">
              <span className="text-gray-600">
                Total Spent: <span className="font-semibold text-gray-900">${capexData.total_spent.toFixed(2)}</span>
              </span>
              <span className="text-gray-600">
                Items: <span className="font-semibold text-gray-900">{capexData.total_count}</span>
              </span>
            </div>
            {capexData.by_category.length > 1 && (
              <div className="flex flex-wrap gap-3">
                {capexData.by_category.map(cat => (
                  <span key={cat.category} className="inline-flex items-center bg-green-50 text-green-800 text-xs font-medium px-2.5 py-1 rounded">
                    {cat.category}: ${cat.total.toFixed(2)} ({cat.count})
                  </span>
                ))}
              </div>
            )}
            <ScrollHint>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Equipment</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {capexData.items.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        <Link to={`/maintenance/work-order/${item.id}`} className="text-blue-600 hover:text-blue-800">
                          {item.title}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.category}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(item.completed_date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.total_cost > 0 ? `$${item.total_cost.toFixed(2)}` : '-'}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-bold">
                    <td colSpan="3" className="px-6 py-3 text-sm text-gray-900 text-right">Total:</td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">${capexData.total_spent.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </ScrollHint>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            No capital expenditures in this date range.
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenanceDashboard;
